import { Box, Txt } from '../ui/primitives';
import { Icon, type IconName } from '../ui/Icon';
import { groupStyle } from '../theme/statusTheme';
import { num, percent } from '../utils/format';
import type { DistributionSlice, Kpis } from '../api/types';

/**
 * KPI row.
 *
 * "Pending Pickup" is the reason this dashboard exists, so it is the only card
 * that gets the emphasis treatment: a wash of the accent, an accent rail and a
 * larger figure. Everything else stays deliberately quiet so the eye lands on
 * the pending number first. Emphasis, not five competing colours.
 */
interface CardProps {
  label: string;
  value: number;
  icon: IconName;
  hint?: string;
  accent: string;
  emphasis?: boolean;
  /** Hover text spelling out a roll-up's constituent statuses. */
  title?: string;
}

function KpiCard({ label, value, icon, hint, accent, emphasis = false, title }: CardProps): JSX.Element {
  if (emphasis) {
    return (
      <Box className="relative h-full overflow-hidden rounded-card bg-surface-card ring-1 ring-hairline shadow-lift animate-fade-in">
        <Box className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: accent }} />
        <Box className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundColor: accent }} />
        <Box className="relative p-5">
          <Box className="flex items-center gap-2">
            <Box style={{ color: accent }}>
              <Icon name={icon} className="w-[18px] h-[18px]" strokeWidth={2} />
            </Box>
            <Txt className="text-[12px] font-semibold uppercase tracking-[0.07em] text-ink-secondary">{label}</Txt>
          </Box>
          <Txt as="p" className="mt-3 text-[44px] leading-none font-semibold text-ink-primary">
            {num(value)}
          </Txt>
          {hint ? (
            <Txt as="p" className="mt-2 text-[13px] text-ink-secondary">
              {hint}
            </Txt>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      title={title}
      className="h-full rounded-card bg-surface-card ring-1 ring-hairline shadow-card p-5 transition-shadow duration-200 hover:shadow-lift animate-fade-in"
    >
      <Box className="flex items-center gap-2">
        <Box style={{ color: accent }}>
          <Icon name={icon} className="w-[18px] h-[18px]" />
        </Box>
        <Txt className="text-[12px] font-semibold uppercase tracking-[0.07em] text-ink-muted">{label}</Txt>
      </Box>
      <Txt as="p" className="mt-3 text-[30px] leading-none font-semibold text-ink-primary">
        {num(value)}
      </Txt>
      {hint ? (
        <Txt as="p" className="mt-1.5 text-[12px] text-ink-muted">
          {hint}
        </Txt>
      ) : null}
    </Box>
  );
}

export function KpiCards({
  kpis,
  distribution,
}: {
  kpis: Kpis;
  distribution: DistributionSlice[];
}): JSX.Element {
  const share = (n: number) => (kpis.total > 0 ? `${percent((n / kpis.total) * 100)} of orders` : 'No orders');

  /**
   * Roll-up cards name the statuses they contain, so a figure here can always
   * be reconciled against the per-status strip below and against SQL.
   */
  const countOf = (status: string) => distribution.find((d) => d.status === status)?.count ?? 0;
  const parts = (statuses: string[]) =>
    statuses
      .map((s) => ({ label: distribution.find((d) => d.status === s)?.label ?? s, count: countOf(s) }))
      .filter((p) => p.count > 0)
      .map((p) => `${p.label} ${num(p.count)}`)
      .join(' · ');

  const returnedParts = parts(['RTO', 'RTO-Delivered']);
  const pickedUpParts = parts([
    'Dispatched',
    'InTransit',
    'OutForDelivery',
    'Delivered',
    'NDR',
    'NDR-Provided',
    'NDR-Submitted',
    'Lost',
    'RTO',
    'RTO-Delivered',
  ]);

  return (
    <Box className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <Box className="sm:col-span-2 lg:col-span-1">
        <KpiCard
          label="Pending Pickup"
          value={kpis.pendingPickup}
          icon="clock"
          accent="var(--life-pending)"
          emphasis
          hint={
            kpis.total > 0
              ? `${percent(kpis.pendingPickupPercent)} of ${num(kpis.total)} orders still in the warehouse`
              : 'No orders in this period'
          }
        />
      </Box>

      <KpiCard
        label="Total Orders"
        value={kpis.total}
        icon="package"
        accent="var(--ink-secondary)"
        hint="Received in this period"
      />
      <KpiCard
        label="Picked Up"
        value={kpis.pickedUp}
        icon="truck"
        accent="var(--life-transit)"
        hint={share(kpis.pickedUp)}
        title={pickedUpParts ? `Includes: ${pickedUpParts}` : undefined}
      />
      <KpiCard
        label="Delivered"
        value={kpis.delivered}
        icon="check"
        accent="var(--life-delivered)"
        hint={share(kpis.delivered)}
      />
      {/*
        RTO and Cancelled are shown separately, not rolled into one
        "Cancelled / Failed" figure. They are different operational problems: an
        RTO travelled, failed delivery and is coming back (a courier/address
        issue), while a cancellation never shipped at all (an order issue).
        Combining them hid which one was actually happening.
      */}
      <KpiCard
        label="Returned (RTO)"
        value={kpis.returned}
        icon="undo"
        accent="var(--life-returned)"
        hint={returnedParts || share(kpis.returned)}
      />
      <KpiCard
        label="Cancelled"
        value={kpis.cancelled}
        icon="x"
        accent="var(--life-cancelled)"
        hint={share(kpis.cancelled)}
      />
    </Box>
  );
}

/**
 * Per-status strip — one tile per actual `order_status`, never a roll-up.
 *
 * This replaced two grouped tiles ("In transit", "Exceptions (NDR)") that were
 * genuinely misleading: "In transit 6,136" read like the `InTransit` status but
 * was Dispatched + InTransit + OutForDelivery, while the `InTransit` status
 * alone was 4,682. Anyone cross-checking against SQL saw two different numbers
 * for what looked like the same thing.
 *
 * Every figure here now maps one-to-one onto a status in the database. The six
 * cards above remain deliberate roll-ups, and each states what it contains.
 */
export function StatusBreakdown({
  distribution,
  total,
}: {
  distribution: DistributionSlice[];
  total: number;
}): JSX.Element | null {
  if (!distribution.length) return null;

  return (
    <Box>
      <Txt as="p" className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted">
        By status
      </Txt>
      {/*
        EVERY status is rendered, including zero-count ones.
        Filtering to `count > 0` made the strip change shape with the data:
        pick a quiet day and most tiles vanished, leaving a stray tile in a wide
        empty row. Worse, a missing tile is ambiguous — the reader cannot tell
        "no orders in that state" from "that state is not tracked". A zero says
        it outright, and the layout stays put when switching ranges.

        Each tile carries its own ring rather than relying on a hairline-coloured
        parent showing through a 1px grid gap: with an incomplete final row that
        technique painted the leftover cells solid grey.
      */}
      <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {distribution.map((slice) => {
          const style = groupStyle(slice.group);
          const empty = slice.count === 0;
          return (
            <Box
              key={slice.status}
              title={`${slice.status} — ${empty ? 'no orders in this period' : `${num(slice.count)} orders`}`}
              className={[
                'rounded-xl bg-surface-card ring-1 ring-hairline px-4 py-3 transition-opacity duration-150',
                // Zeros stay legible but recede, so the eye lands on real volume.
                empty ? 'opacity-45' : '',
              ].join(' ')}
            >
              <Box className="flex items-center gap-2">
                <Box
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: style.fill }}
                  aria-hidden
                />
                <Txt className="text-[12px] text-ink-muted truncate">{slice.label}</Txt>
              </Box>
              <Box className="mt-1 flex items-baseline gap-1.5">
                <Txt as="p" className="text-[19px] font-semibold text-ink-primary tabular">
                  {num(slice.count)}
                </Txt>
                <Txt className="text-[11px] text-ink-muted tabular">
                  {percent((slice.count / Math.max(1, total)) * 100, 1)}
                </Txt>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
